import {
  FOLDER_ROOT_PATH,
  FolderPath,
  folderPathName,
  isSameOrDescendantFolderPath,
  parentFolderPath,
  repathFolderPath,
} from '@lib/library/folderPath';
import { showToast } from '@lib/utils/toastmaster';
import { getLibraryUrl } from '@lib/utils/url';
import {
  FolderMoveSource,
  LibraryFolderStore,
  MediaMove,
  MediaMoveSource,
  useLibraryFolder,
} from '@stores/Library/LibraryFolder';
import { observer } from 'mobx-react';
import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderDeleteDialog } from './FolderDeleteDialog';
import { FolderEditDialog, FolderEditTarget } from './FolderEditDialog';
import { FolderPickerDialog, FolderPickerPurpose } from './FolderPickerDialog';

type OpenFolderDialog =
  | { kind: 'edit'; target: FolderEditTarget }
  | { kind: 'pick'; purpose: FolderPickerPurpose }
  | { kind: 'delete'; path: FolderPath };

export type FolderDialogs = {
  createFolder: (parent: FolderPath) => void;
  editFolder: (path: FolderPath) => void;
  moveFolder: (path: FolderPath) => void;
  deleteFolder: (path: FolderPath) => void;
  /** Opens the picker for these media; `via` is the control that asked, for tracking. */
  moveMedia: (mediaIds: string[], via: Exclude<MediaMoveSource, 'drag'>) => void;
};

const FolderDialogsContext = createContext<FolderDialogs | null>(null);

export const useFolderDialogs = () => {
  const context = useContext(FolderDialogsContext);
  if (!context) throw new Error('useFolderDialogs must be used within a FolderDialogsProvider');
  return context;
};

export type OpenFolderDialogFn = (dialog: OpenFolderDialog) => void;

type OpenRef = MutableRefObject<OpenFolderDialogFn | null>;

/**
 * Hands out the folder dialogs without holding their state, so opening one re-renders only
 * `FolderDialogsHost` and not the grid under it — the same split as `MediaDeleteModalProvider`.
 */
export const FolderDialogsProvider = ({
  openRef,
  children,
}: {
  openRef: OpenRef;
  children: ReactNode;
}) => {
  const dialogs = useMemo<FolderDialogs>(() => {
    const open = (dialog: OpenFolderDialog) => openRef.current?.(dialog);
    return {
      createFolder: (parent) => open({ kind: 'edit', target: { kind: 'create', parent } }),
      editFolder: (path) => open({ kind: 'edit', target: { kind: 'edit', path } }),
      moveFolder: (path) => open({ kind: 'pick', purpose: { kind: 'folder', path } }),
      deleteFolder: (path) => open({ kind: 'delete', path }),
      moveMedia: (mediaIds, via) =>
        open({ kind: 'pick', purpose: { kind: 'media', mediaIds, via } }),
    };
  }, [openRef]);

  return <FolderDialogsContext.Provider value={dialogs}>{children}</FolderDialogsContext.Provider>;
};

/** The library page opened on `path`. */
export const folderUrl = (folder: LibraryFolderStore, path: FolderPath) =>
  getLibraryUrl(folder.library.libraryId, folder.library.name, { folderPath: path });

/** What to call a folder in copy: its name, or the library's for the root. */
export const folderDisplayName = (folder: LibraryFolderStore, path: FolderPath) =>
  path === FOLDER_ROOT_PATH ? (folder.library.name ?? 'Library') : folderPathName(path);

/** Called with a folder's path before and after a rename or move. */
export type FollowFolderRelocation = (from: FolderPath, to: FolderPath) => void;

/**
 * Keeps the URL on the folder on screen when it, or a folder above it, is renamed or moved —
 * otherwise `FolderRouteSync` would fall back to the nearest folder that still exists.
 */
export const useFollowFolderRelocation = (folder: LibraryFolderStore): FollowFolderRelocation => {
  const navigate = useNavigate();
  return useCallback(
    (from, to) => {
      const current = folder.currentFolderPath;
      if (!isSameOrDescendantFolderPath(current, from)) return;
      navigate(folderUrl(folder, repathFolderPath(current, from, to)), { replace: true });
    },
    [folder, navigate],
  );
};

/** Puts media back where a move into `destination` took them from, and says when some didn't go. */
const undoMediaMoveWithFeedback = async (
  folder: LibraryFolderStore,
  move: MediaMove,
  destination: FolderPath,
) => {
  const { restored, stranded, error } = await folder.undoMediaMove(move);
  if (stranded === 0) return;
  if (restored === 0) {
    showToast('folder-action-failed', { action: 'undo', error });
  } else {
    showToast('media-move-undo-incomplete', {
      restored,
      stranded,
      destination: folderDisplayName(folder, destination),
      error,
    });
  }
};

/**
 * Moves media and says so, with an "Undo". Shared by the picker and by a drop onto a folder, so
 * both report the same way.
 */
export const moveMediaWithFeedback = async (
  folder: LibraryFolderStore,
  mediaIds: string[],
  destination: FolderPath,
  via: MediaMoveSource,
) => {
  try {
    const move = await folder.moveMedia(mediaIds, destination, via);
    if (move.count > 0) {
      showToast('media-moved-to-folder', {
        count: move.count,
        destination: folderDisplayName(folder, destination),
        undo: () => void undoMediaMoveWithFeedback(folder, move, destination),
      });
    }
  } catch (error) {
    showToast('folder-action-failed', { action: 'move-media', error });
  }
};

/**
 * Moves a folder into another and says so, with an "Undo", from the menu's "Move to…" or the
 * drop of a folder pill. Returns where it went, or `null` when it could not be moved.
 */
export const moveFolderWithFeedback = async (
  folder: LibraryFolderStore,
  path: FolderPath,
  destination: FolderPath,
  via: FolderMoveSource,
  followRelocation: FollowFolderRelocation,
): Promise<FolderPath | null> => {
  let moved: FolderPath;
  try {
    moved = await folder.updateFolder(path, { parent: destination }, via);
  } catch (error) {
    showToast('folder-action-failed', { action: 'move', error });
    return null;
  }
  followRelocation(path, moved);

  const undo = async () => {
    try {
      await folder.updateFolder(moved, { parent: parentFolderPath(path) }, 'undo');
      followRelocation(moved, path);
    } catch (error) {
      showToast('folder-action-failed', { action: 'undo', error });
    }
  };
  showToast('folder-moved', {
    name: folderPathName(path),
    destination: folderDisplayName(folder, destination),
    undo: () => void undo(),
  });
  return moved;
};

export const FolderDialogsHost = observer(function FolderDialogsHost({
  openRef,
}: {
  openRef: OpenRef;
}) {
  const folder = useLibraryFolder();
  const navigate = useNavigate();
  const followRelocation = useFollowFolderRelocation(folder);
  const [dialog, setDialog] = useState<OpenFolderDialog | null>(null);

  useEffect(() => {
    openRef.current = setDialog;
    return () => {
      openRef.current = null;
    };
  }, [openRef]);

  const close = () => setDialog(null);

  const openFolder = (path: FolderPath) => navigate(folderUrl(folder, path), { replace: true });

  if (!dialog) return null;

  switch (dialog.kind) {
    case 'edit': {
      const { target } = dialog;
      return (
        <FolderEditDialog
          folder={folder}
          target={target}
          onClose={close}
          onSaved={(path) => target.kind === 'edit' && followRelocation(target.path, path)}
        />
      );
    }

    case 'pick': {
      const { purpose } = dialog;
      return (
        <FolderPickerDialog
          folder={folder}
          purpose={purpose}
          onClose={close}
          onPick={async (destination) => {
            if (purpose.kind === 'media') {
              await moveMediaWithFeedback(folder, purpose.mediaIds, destination, purpose.via);
              close();
              return;
            }
            const moved = await moveFolderWithFeedback(
              folder,
              purpose.path,
              destination,
              'menu',
              followRelocation,
            );
            if (moved) close();
          }}
        />
      );
    }

    case 'delete': {
      const { path } = dialog;
      return (
        <FolderDeleteDialog
          folder={folder}
          path={path}
          onClose={close}
          onConfirm={async (keepMedia) => {
            try {
              const name = folderPathName(path);
              const parent = parentFolderPath(path);
              const { deletedMediaCount, keptMediaCount } = await folder.deleteFolder(path, {
                keepMedia,
              });
              if (isSameOrDescendantFolderPath(folder.currentFolderPath, path)) {
                openFolder(parent);
              }
              showToast('folder-deleted', {
                name,
                mediaCount: deletedMediaCount,
                kept: { count: keptMediaCount, destination: folderDisplayName(folder, parent) },
              });
              close();
            } catch (error) {
              showToast('folder-action-failed', { action: 'delete', error });
            }
          }}
        />
      );
    }
  }
});
